/**
 * Hook to manage chat messages with Redux as single source of truth.
 * Orchestrates message syncing, selection, and sending.
 */

import { clearConversation, resetPagination } from '@saaga/shared/store/slices/chat.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { useEffect } from 'react'

import type { ChatItem, ChatUserInteraction } from '@/components/chat/chat-message.interface'
import { useBalanceRefreshOnStream } from '@/hooks/billing/use-balance-refresh-on-stream.hook'
import type { UseAgentsWebsocketConfigReturn } from '@/hooks/use-agents-websocket-config.hook'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

import { useChatMessageSelectors } from './use-chat-message-selectors.hook'
import { useMessageSend } from './use-chat-message-send.hook'
import { useChatMessageSync } from './use-chat-message-sync.hook'
import { useIncomingMessageSound } from './use-incoming-message-sound.hook'
import { useStreamCallbacks } from './use-stream-callbacks.hook'

interface UseChatMessagesParams {
  conversationId: string | null
  agencySlug: string | null
  userLookup: UserLookupMap
  onNewConversation: (newConversationId: string) => void
  websocket?: UseAgentsWebsocketConfigReturn
  userInteractionCardList: ChatUserInteraction[]
}

export interface UseChatMessagesReturn {
  itemList: ChatItem[]
  isLoading: boolean
  isFetching: boolean
  isFetchingMoreMessages: boolean
  isSending: boolean
  hasMoreMessages: boolean
  loadMoreMessages: () => void
  sendNote: (content: string) => void
  sendMessage: (content: string, attachmentIdList?: string[]) => void
}

export const useChatMessages = ({
  conversationId,
  agencySlug,
  userLookup,
  onNewConversation,
  websocket,
  userInteractionCardList,
}: UseChatMessagesParams): UseChatMessagesReturn => {
  const dispatch = useAppDispatch()

  const sync = useChatMessageSync({
    conversationId,
    userLookup,
  })

  const { itemList, hasMoreMessages } = useChatMessageSelectors({
    conversationId,
    userInteractionCardList,
  })

  const send = useMessageSend({
    conversationId,
    agencySlug,
    onNewConversation,
    websocket,
  })

  const onConversationComplete = useBalanceRefreshOnStream()
  useStreamCallbacks({ onNewConversation, onConversationComplete })
  useIncomingMessageSound(conversationId)

  useEffect(() => {
    if (conversationId) {
      dispatch(clearConversation({ conversationId: 'pending' }))
    }

    if (!conversationId) return

    return () => {
      dispatch(clearConversation({ conversationId }))
      dispatch(resetPagination({ conversationId }))
    }
  }, [conversationId, dispatch])

  return {
    itemList,
    isLoading: sync.isLoading,
    isFetching: sync.isFetching,
    isFetchingMoreMessages: sync.isFetching && !sync.isLoading,
    isSending: send.isSending,
    hasMoreMessages,
    loadMoreMessages: sync.loadMoreMessages,
    sendNote: send.sendNote,
    sendMessage: send.sendMessage,
  }
}
